import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Location} from '@angular/common';

import {ProjectViewModel} from '../../../features/user/garage/project.view-model';
import {MenuItem} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '../../modal/modal.service';
import {Camera} from '@capacitor/camera';
import {PostTypeEnum} from '../../../core/enums/post-type.enum';
import {PostViewModel} from '../../timeline/post.view-model';
import {TimelineComponent} from '../../timeline/timeline.component';
import {TimelinePageResult} from '../../timeline/timeline.data-provider.service';
import {CurrentUserViewModel} from '../../../core/models/current-user.view-model';
import {TokenDataProviderService} from '../../../core/services/token-data-provider.service';
import {ActivatedRoute} from '@angular/router';
import {EventApiModel} from '../../../api/models/event.api.model';
import {CustomCameraMediaItem} from '../../../features/custom-camera/custom-camera';
import {CustomCameraService} from '../../../features/custom-camera/custom-camera.service';
import {ActionsService} from '../../footer/actions.service';
import {ViewTypeEnum} from '../../../core/enums/view-type.enum';
import {AbstractComponent} from "../../../core/components/abstract-component";
import {FlutterMediaComposerService} from '../../../core/mobile/flutter-media-composer.service';
import {Subscription} from 'rxjs';


export type MediaFromType = 'cameraMedia' | 'deviceMedia';

@Component({
  selector: 'app-media-grid',
  templateUrl: './media-grid.component.html',
  styleUrls: ['./media-grid.component.scss']
})
export class MediaGridComponent extends AbstractComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  get ownerPermissions(): string[] {
    return this._ownerPermissions;
  }

  @Input() set ownerPermissions(value: string[]) {
    this._ownerPermissions = value;
  }
  get type(): ViewTypeEnum {
    return this._type;
  }

  @Input() set type(value: ViewTypeEnum) {
    this._type = value;
  }

  get items(): MenuItem[] {
    return this._items;
  }

  set items(value: MenuItem[]) {
    this._items = value;
  }

  @Input() public postTypes: PostTypeEnum[] = [];
  @Input() public projectVM: ProjectViewModel;
  @Input() public eventAM: EventApiModel;
  @Input() public sidebarActive: boolean = true;
  @Input() public keepParentActions = false;
  @Input() public syncViewWithUrl = true;
  @Input() public loading = false;
  public readonly loadingPage: TimelinePageResult = {
    records: Array.from({length: 9}, () => new PostViewModel()),
    total: 0,
    page: 1,
  };
  @ViewChild('fileInput') fileInput: ElementRef;
  @Output() public isList: EventEmitter<boolean> = new EventEmitter();
  @ViewChild(TimelineComponent) timeline: TimelineComponent;
  public isSidebarOpened = false;
  public sidebarAction: MediaFromType|null;
  public posts: PostViewModel[]|{isPlaceholder: boolean}[] = [];
  public startIndex = 0;
  public currentUser: CurrentUserViewModel;
  private _type: ViewTypeEnum = ViewTypeEnum.GRID;
  private _items: MenuItem[] = [];
  private _ownerPermissions: string[];
  private addActionSubscription?: Subscription;
  private viewActionSubscription?: Subscription;

  constructor(
    public readonly actionsService: ActionsService,
    private readonly translate: TranslateService,
    private readonly ms: ModalService,
    private readonly tokenDPS: TokenDataProviderService,
    private readonly route: ActivatedRoute,
    private readonly ccs: CustomCameraService,
    private readonly location: Location,
    private readonly flutterMediaComposer: FlutterMediaComposerService,
  ) {
    super();
    const view = this.route.snapshot.queryParams?.view as ViewTypeEnum | undefined;
    if (view) {
      this._type = view;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sidebarActive'] && !changes['sidebarActive'].firstChange) {
      this.syncActiveActions();
    }
  }

  ngOnInit(): void {
    this.subscriptions.push(this.tokenDPS.currentUser.subscribe((u: CurrentUserViewModel) => this.currentUser = u));
    this.initMenuItems();
    this.handleViewQueryParameter();
    this.subscriptions.push(this.route.queryParams.subscribe(({view}: {view?: ViewTypeEnum}) => {
      if (view && view !== this.type) {
        this.type = view;
        if (this.sidebarActive) {
          this.actionsService.viewType = this.type;
        }
        this.isList.emit(this.type === ViewTypeEnum.LIST);
      }
    }));
    this.syncActiveActions();
  }

  ngAfterViewInit() {
      if (this.sidebarActive) {
        this.actionsService.permissions = this.ownerPermissions;
      }
  }

  ngOnDestroy(): void {
    this.releaseActiveActions();
    super.ngOnDestroy();
    this.closeContextMenu();
    if (!this.keepParentActions) {
      this.actionsService.unsubscribeActions();
    }
  }

  closeContextMenu(): void {
    this.isSidebarOpened = false;
    this.sidebarAction = null;
  }

  initMenuItems(): void {
    this.items = [
      {
        label: this.translate.instant('common.gallery'),
        icon: 'pi pi-fw pi-info-circle',
        command: async () => {
          this.closeContextMenu();
          if (this.flutterMediaComposer.canPresent) {
            await this.flutterMediaComposer.open('gallery', this.flutterComposerContext());
            return;
          }
          this.fileInput.nativeElement.click();
          //await this.pickImages();
        },
      },
      {
        label: this.translate.instant('common.add_material'),
        icon: 'pi pi-fw pi-info-circle',
        command: async () => {
          this.closeContextMenu();
          if (this.flutterMediaComposer.canPresent) {
            await this.flutterMediaComposer.open('camera', this.flutterComposerContext());
            return;
          }
          this.ms
            .showMediaModal({projectVM: this.projectVM, eventAM: this.eventAM, media: null, isImagePicked: false, timeline: this.timeline})
            .successState.subscribe(() =>  {
          });
        },
      }
    ];
  }

  public handleViewQueryParameter(): void {
    const {view} = this.route.snapshot.queryParams;
    if (view) {
      this.type = view;
      if (this.sidebarActive) {
        this.actionsService.viewType = this.type;
      }
      // this.isList.emit(false);
    }
  }

  fileChangeEvent(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = (event.target.files[0] as File);
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const file = e.target.result as string;
        const fileType = file.includes('image') ? 'image' : 'video';
        const media: CustomCameraMediaItem = await this.ccs.mediaItemFromFilePath(file, fileType);
        this.subscriptions.push(this.ms
          .showMediaModal({
            projectVM: this.projectVM,
            eventAM: this.eventAM,
            media,
            isImagePicked: true,
            timeline: this.timeline
          })
          .successState.subscribe(() => {
          }));
      };
      reader.readAsDataURL(file);
    }
  }

  async pickImages() {
    const {photos} = await Camera.pickImages({
      quality: 100,
      limit: 1,
    });
    const media: CustomCameraMediaItem = await this.ccs.mediaItemFromFilePath(photos[0].webPath, 'image');
    this.subscriptions.push(this.ms
      .showMediaModal({projectVM: this.projectVM, eventAM: this.eventAM, media, isImagePicked: true, timeline: this.timeline})
      .successState.subscribe(() =>  {
    }));
  }

  showAsList(): void {
    this.type = ViewTypeEnum.LIST;
    this.isList.emit(true);
    this.updateViewQuery(ViewTypeEnum.LIST);
  }

  showAsGrid(): void {
    this.type = ViewTypeEnum.GRID;
    this.isList.emit(false);
    this.updateViewQuery(ViewTypeEnum.GRID);
  }

  private subscribeToViewChangeAction(): void {
    this.viewActionSubscription = this.actionsService.subscribeToViewChangeAction(this.showAs.bind(this));
  }

  public subscribeToAddAction(): void {
    this.addActionSubscription = this.actionsService.subscribeToAddAction((args: {isOpened: boolean}) => {
      if (this.sidebarActive) {
        this.isSidebarOpened = args.isOpened;
      }
    });
  }

  private syncActiveActions(): void {
    if (!this.sidebarActive) {
      this.releaseActiveActions();
      this.closeContextMenu();
      return;
    }
    if (!this.addActionSubscription) {
      this.subscribeToAddAction();
    }
    if (!this.viewActionSubscription) {
      this.subscribeToViewChangeAction();
    }
    this.actionsService.permissions = this.ownerPermissions;
    this.actionsService.viewType = this.type;
  }

  private releaseActiveActions(): void {
    if (this.addActionSubscription) {
      this.actionsService.unsubscribeAction(this.addActionSubscription);
      this.addActionSubscription = undefined;
    }
    if (this.viewActionSubscription) {
      this.actionsService.unsubscribeAction(this.viewActionSubscription);
      this.viewActionSubscription = undefined;
    }
  }

  private showAs(viewType: ViewTypeEnum): void {
    if (!this.sidebarActive) {
      return;
    }
    switch (viewType) {
      case ViewTypeEnum.GRID: return this.showAsGrid();
      case ViewTypeEnum.LIST: return this.showAsList();
    }
  }

  private updateViewQuery(viewType: ViewTypeEnum): void {
    const nextUrl = this.withQueryParamInCurrentUrl('view', viewType, true);
    this.location.replaceState(nextUrl);
  }

  private withQueryParamInCurrentUrl(key: string, value: string, clearFragment: boolean): string {
    const currentUrl = this.location.path(true);
    const hashIndex = currentUrl.indexOf('#');
    const pathAndQuery = hashIndex >= 0 ? currentUrl.slice(0, hashIndex) : currentUrl;
    const queryIndex = pathAndQuery.indexOf('?');
    const path = queryIndex >= 0 ? pathAndQuery.slice(0, queryIndex) : pathAndQuery;
    const queryString = queryIndex >= 0 ? pathAndQuery.slice(queryIndex + 1) : '';
    const params = new URLSearchParams(queryString);
    params.set(key, value);
    const nextQuery = params.toString();
    return `${path}${nextQuery ? `?${nextQuery}` : ''}${clearFragment ? '' : (hashIndex >= 0 ? currentUrl.slice(hashIndex) : '')}`;
  }

  public forceRefreshTimelineFromParent(): void {
    if (this.loading) {
      return;
    }
    this.timeline?.forceRefreshFromParent();
  }

  private flutterComposerContext() {
    return {
      projectVM: this.projectVM,
      eventAM: this.eventAM,
      timeline: this.timeline,
      currentUser: this.currentUser,
    };
  }
}
